import styled from "styled-components";
import React, { useCallback, useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { AddDateTitle } from "../App";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import ArrowLeftIcon from "@material-ui/icons/ArrowLeft";

import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import GetAppIcon from "@material-ui/icons/GetApp";
import ShareIcon from "@mui/icons-material/Share";
import ArrowBackOutlinedIcon from "@material-ui/icons/ArrowBackOutlined";
import { ViewerInfo } from "./ViewerInfo";
import { ViewStore, ItemsStore, ScrollStore } from "./Store";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import EditIcon from "@mui/icons-material/Edit";
import AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { slugify, getDomain } from "../components/Utils";
import archieExports from "../archie-exports";
import { ShareModal } from "./ShareModal";
import { GetSearchResultsForItem } from "./SearchEngine";
import TextField from "@mui/material/TextField";
import { API } from "aws-amplify";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
export function Viewer() {
  const { view, setView } = ViewStore();
  const navigate = useNavigate();

  const { items } = ItemsStore();
  const [numPages, setNumPages] = useState(null);
  const [showAllPages, setShowAllPages] = useState(false);
  const [showInfoView, setShowInfoView] = useState(false);
  const [editInfo, setEditInfo] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const [startX, setStartX] = useState(0);
  const [endX, setEndX] = useState(0);

  const imgRef = React.useRef(null);
  const [recognitionResults, setRecognitionResults] = useState([]);
  const [imageDimensions, setImageDimensions] = useState(null);
  const [loadingImg, setLoadingImg] = useState(true);
  const [progress, setProgress] = useState(1);
  const [tagging, setTagging] = useState({
    active: false,
    boxIdx: null,
    subject: "",
  });
  const [pendingTagBoxKey, setPendingTagBoxKey] = useState(null);
  const [hoveredFaceIdx, setHoveredFaceIdx] = useState(null);

  const [zoom, setZoom] = useState(false);
  const [params, setSearchParams] = useSearchParams();

  const { setScrollPosition } = ScrollStore();
  const { route, user } = useAuthenticator((context) => [
    context.route,
    context.user,
  ]);
  const isAuthenticated = route === "authenticated" && Boolean(user);
  const [viewUrl, setViewUrl] = useState("");
  const location = useLocation();

  useEffect(() => {
    if (!imgRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      setImageDimensions((prev) => (prev ? { ...prev } : prev));
    });
    resizeObserver.observe(imgRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Helper to call the new GET endpoint
  const recognize = async (imageUrl) => {
    try {
      // URL encode the image link to handle special characters
      const encodedUrl = encodeURIComponent(imageUrl);

      // Call the new GET endpoint using query parameters
      const response = await fetch(
        `https://${process.env.REACT_APP_API_ENDPOINT}/faces?imageUrl=${encodedUrl}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Recognition failed: ${response.status}`);
      }

      const result = await response.json();
      const normalizeFaceResult = (face) => {
        const area = face?.facialArea;
        if (!area) return null;

        const bestMatch = face?.bestMatch;
        const candidates = Array.isArray(face?.candidates)
          ? face.candidates
          : [];
        const subjects = bestMatch
          ? [
              bestMatch,
              ...candidates.filter(
                (candidate) => candidate.faceId !== bestMatch.faceId,
              ),
            ]
          : candidates;

        return {
          box: {
            x_min: area.x,
            y_min: area.y,
            x_max: area.x + area.w,
            y_max: area.y + area.h,
          },
          subjects,
          queryFaceIndex: face?.queryFaceIndex,
          faceConfidence: face?.faceConfidence,
        };
      };

      const normalizedResults = Array.isArray(result?.faces)
        ? result.faces.map(normalizeFaceResult).filter(Boolean)
        : result?.result || [];

      setRecognitionResults(normalizedResults);
    } catch (error) {
      console.error("recognition error:", error?.message || "");
      setRecognitionResults([]);
    }
  };

  const tagFace = async ({ imageUrl, box, subject }) => {
    const boxKey = getBoxKey(box);
    setPendingTagBoxKey(boxKey);
    try {
      // const response = await fetch("http://localhost:4000/faces", {
      //   method: "PUT",
      //   headers: {
      //     "Content-Type": "application/json",
      //     Accept: "application/json",
      //   },
      //   body: JSON.stringify({ imageUrl, box, subject }),
      // });
      await API.put("archie_api", "/faces", {
        headers: { "Content-Type": "application/json" },
        body: { imageUrl, box, subject },
      });

      setTagging({ active: false, boxIdx: null, subject: "" });
      await recognize(imageUrl);
      setPendingTagBoxKey(null);
    } catch (err) {
      setPendingTagBoxKey(null);
      console.error("tagFace error", err);
    }
  };

  const getBoxKey = useCallback(
    (box) => [box.x_min, box.y_min, box.x_max, box.y_max].join(":"),
    [],
  );

  const getTagWidth = useCallback((text, minWidth) => {
    const fontSize = parseFloat(getComputedStyle(document.body).fontSize) || 16;
    const averageCharWidth = fontSize * 0.6;
    const padding = fontSize * 0.6;
    const contentWidth = (text || "").length * averageCharWidth + padding;
    return Math.max(contentWidth, minWidth);
  }, []);

  const updateLoadingImg = async (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    setImageDimensions({
      naturalWidth,
      naturalHeight,
    });
    recognize(getAssetUrl(view));
    setLoadingImg(false);
  };
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (view) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    // Cleanup function to restore scroll on component unmount
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [view]);

  useEffect(() => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    function handleResize() {
      if (window.innerWidth > 500) {
        setShowInfoView(true);
      }
    }

    if (window.innerWidth > 500) {
      setShowInfoView(true);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const guid = React.useMemo(
    () => location.pathname.split("/")[1] || "",
    [location.pathname],
  );

  useEffect(() => {
    if (view || !guid) return;

    const fetchDoc = async () => {
      try {
        const docs = await GetSearchResultsForItem(guid);
        if (!docs?.length) throw new Error("Not found");
        setView(docs[0]);
      } catch (err) {
        navigate("/");
      }
    };

    fetchDoc();
  }, [guid, view, navigate, setView]);

  useEffect(() => {
    if (!view) return;
    setTagging({ active: false, boxIdx: null, subject: "" });
    setPendingTagBoxKey(null);
    setHoveredFaceIdx(null);
    const slug = slugify(view.dcTitle || "");
    setViewUrl(`${getDomain()}/${view.id}/${slug}`);
  }, [view]);

  function onDocumentLoadSuccess({ numPages }) {
    setLoadingImg(false);
    setNumPages(numPages);
    setShowAllPages(true);
  }
  function onDocumentLoading({ loaded, total }) {
    setProgress(Math.floor((loaded / total) * 100));
  }

  const handleCloseClick = () => {
    setImageDimensions((prev) => ({ ...prev }));

    if (!params.size && view) {
      const url =
        "/search?searchTermModifier=any&firstRow=1&numberOfRows=50&sortField=dcDate&sortOrder=desc&dcTypeFilter=&searchTerm=" +
        view.dcTitle;
      setView(null);
      navigate(url);
      return;
    }

    setView(null);
    setSearchParams(params);
    setScrollPosition(true);
  };

  const handleInfoClick = () => {
    setShowInfoView(true);
  };

  const handleEditInfoClick = () => {
    setShowInfoView(true);
    setEditInfo(true);
  };

  const showAll = (scale) => {
    const pages = [];
    if (numPages > 0) {
      for (let i = 1; i <= numPages; i++) {
        pages.push(<StyledPage pageNumber={i} scale={scale} />);
      }

      return pages;
    }
  };

  const getAssetUrl = (view) => {
    let S3OriginalsUrl = "";
    switch (view.dcAccessRights) {
      case "public":
        S3OriginalsUrl =
          archieExports["public_assets_bucket"] +
          "/originals/" +
          view.id +
          "." +
          view.dcFormat;
        break;
      case "private":
        S3OriginalsUrl =
          archieExports["private_assets_bucket"] +
          "/originals/" +
          view.id +
          "." +
          view.dcFormat;

        break;

      default:
        S3OriginalsUrl =
          "https://archie-beeri-" +
          view.dcAccessRights +
          ".s3-eu-west-1.amazonaws.com/originals/" +
          view.id +
          "." +
          view.dcFormat;
    }
    return S3OriginalsUrl;
  };
  const handleClick = useCallback(
    (goBack) => {
      if (!items?.length || !view) return;
      setRecognitionResults([]);
      setLoadingImg(true);
      setPendingTagBoxKey(null);
      let current = items.findIndex((item) => item.id === view.id);
      if (goBack) {
        if (current === items.length - 1) {
          setView(items[0]);
        } else {
          setView(items[current + 1]);
        }
      } else {
        if (current === 0) {
          setView(items[items.length - 1]);
        } else {
          setView(items[current - 1]);
        }
      }
    },
    [items, setView, view],
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!view || !items || items.length <= 1) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        handleClick(true);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        handleClick(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleClick, items, view]);

  const mainAsset = (view, startX, setStartX, endX, setEndX) => {
    const S3OriginalsUrl = getAssetUrl(view);

    const swipeHandler = () => {
      if (startX - endX > 150) {
        handleClick(true);
      }

      if (startX - endX < -150) {
        handleClick(false);
      }
    };
    switch (view.dcFormat) {
      case "wav":
      case "mp3":
        return (
          <ImgContainer>
            {items.length > 1 ? (
              <ArrowCircleRight onClick={() => handleClick(false)}>
                <ArrowRightIconWrapper />
              </ArrowCircleRight>
            ) : null}
            <ImgWrapper>
              <Audio
                progressJumpSteps={{ backward: 30000, forward: 30000 }}
                autoPlay
                showJumpControls={true}
                onTouchStart={(e) => {
                  setStartX(e.changedTouches[0].screenX);
                }}
                onTouchEnd={(e) => {
                  swipeHandler();
                }}
                onTouchMove={(e) => {
                  setEndX(e.targetTouches[0].clientX);
                }}
                src={S3OriginalsUrl}
              ></Audio>
            </ImgWrapper>
            {items.length > 1 ? (
              <ArrowCircleLeft onClick={() => handleClick(true)}>
                <ArrowLeftIconWrapper />
              </ArrowCircleLeft>
            ) : null}
          </ImgContainer>
        );
      case "mp4":
      case "m4v":
      case "mov":
      case "ogv":
        return (
          <ImgContainer>
            {items.length > 1 ? (
              <ArrowCircleRight onClick={() => handleClick(false)}>
                <ArrowRightIconWrapper />
              </ArrowCircleRight>
            ) : null}
            <ImgWrapper>
              <Video
                autoPlay
                onTouchStart={(e) => {
                  setStartX(e.changedTouches[0].screenX);
                }}
                onTouchEnd={(e) => {
                  swipeHandler();
                }}
                onTouchMove={(e) => {
                  setEndX(e.targetTouches[0].clientX);
                }}
                preload="metadata"
                controls
                src={S3OriginalsUrl}
              ></Video>
            </ImgWrapper>
            {items.length > 1 ? (
              <ArrowCircleLeft onClick={() => handleClick(true)}>
                <ArrowLeftIconWrapper />
              </ArrowCircleLeft>
            ) : null}
          </ImgContainer>
        );
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
      case "webp":
        return (
          <ImgContainer>
            {items.length > 1 ? (
              <ArrowCircleRight onClick={() => handleClick(false)}>
                <ArrowRightIconWrapper />
              </ArrowCircleRight>
            ) : null}
            <ImgWrapper>
              <CircularProgress
                style={{ display: loadingImg ? "block" : "none" }}
              />

              <Img
                ref={imgRef}
                zoom={zoom}
                onClick={() => setZoom(!zoom)}
                style={{ display: loadingImg ? "none" : "block" }}
                onTouchStart={(e) => {
                  setStartX(e.changedTouches[0].screenX);
                }}
                onTouchEnd={(e) => {
                  swipeHandler();
                }}
                onTouchMove={(e) => {
                  setEndX(e.targetTouches[0].clientX);
                }}
                src={S3OriginalsUrl}
                onLoad={updateLoadingImg}
                alt="תמונת מוצג"
              />
              {imageDimensions &&
                recognitionResults.map((result, index) => {
                  const { box, subjects } = result;
                  const boxKey = getBoxKey(box);
                  const { naturalWidth, naturalHeight } = imageDimensions;
                  const { offsetWidth, offsetHeight } = imgRef.current;

                  const imageAspectRatio = naturalWidth / naturalHeight;
                  const containerAspectRatio = offsetWidth / offsetHeight;

                  let renderedWidth, renderedHeight, offsetX, offsetY;

                  if (imageAspectRatio > containerAspectRatio) {
                    renderedWidth = offsetWidth;
                    renderedHeight = offsetWidth / imageAspectRatio;
                    offsetX = 0;
                    offsetY = (offsetHeight - renderedHeight) / 2;
                  } else {
                    renderedHeight = offsetHeight;
                    renderedWidth = offsetHeight * imageAspectRatio;
                    offsetY = 0;
                    offsetX = (offsetWidth - renderedWidth) / 2;
                  }
                  const extraOffset = window.innerWidth >= 500 ? 60 : 0;

                  const boxStyle = {
                    left: `${
                      (box.x_min / naturalWidth) * renderedWidth +
                      offsetX +
                      extraOffset
                    }px`,
                    top: `${
                      (box.y_min / naturalHeight) * renderedHeight + offsetY
                    }px`,
                    width: `${
                      ((box.x_max - box.x_min) / naturalWidth) * renderedWidth
                    }px`,
                    height: `${
                      ((box.y_max - box.y_min) / naturalHeight) * renderedHeight
                    }px`,
                  };

                  const bestMatch = subjects[0];
                  const similarity = bestMatch?.similarity ?? 0;
                  const subject = bestMatch?.matched
                    ? bestMatch.subject
                    : similarity > 0.95
                      ? bestMatch.subject
                      : null;

                  const activateTagging = (initialSubject = "") => {
                    if (!isAuthenticated) return;
                    setTagging({
                      active: true,
                      boxIdx: index,
                      subject: initialSubject,
                    });
                  };
                  const handleSubmit = () => {
                    if (!isAuthenticated) return;
                    if (!tagging.subject.trim()) return;
                    const submittedSubject = tagging.subject;
                    setTagging({ active: false, boxIdx: null, subject: "" });
                    setHoveredFaceIdx(null);
                    tagFace({
                      imageUrl: S3OriginalsUrl,
                      box,
                      subject: submittedSubject,
                    });
                  };

                  const isEditing =
                    isAuthenticated &&
                    tagging.active &&
                    tagging.boxIdx === index;
                  const isPending = pendingTagBoxKey === boxKey;

                  return (
                    <React.Fragment key={boxKey}>
                      <FaceRegion
                        style={boxStyle}
                        onMouseEnter={() => {
                          setHoveredFaceIdx(index);
                          if (isAuthenticated && !subject && !isPending) {
                            activateTagging();
                          }
                        }}
                        onMouseLeave={() => setHoveredFaceIdx(null)}
                      >
                        <BoundingBox />
                        {isPending ? (
                          <PendingTagIndicator size={16} thickness={5} />
                        ) : subject ? (
                          <SubjectLabel
                            onClick={() => activateTagging(subject || "")}
                          >
                            {subject}
                          </SubjectLabel>
                        ) : null}
                      </FaceRegion>
                      {isAuthenticated &&
                      isEditing &&
                      hoveredFaceIdx === index &&
                      !isPending
                        ? (() => {
                            const boxWidth = parseFloat(boxStyle.width);
                            const boxLeft = parseFloat(boxStyle.left);
                            const desiredWidth = getTagWidth(
                              tagging.subject,
                              boxWidth,
                            );
                            const boundedLeft =
                              boxLeft - (desiredWidth - boxWidth) / 2;
                            return (
                              <TagForm
                                style={{
                                  left: `${boundedLeft}px`,
                                  top: `calc(${boxStyle.top} + ${boxStyle.height} + 4px)`,
                                  width: `${desiredWidth}px`,
                                }}
                              >
                                <TagInput
                                  autoFocus
                                  size="small"
                                  value={tagging.subject}
                                  onChange={(e) =>
                                    setTagging((prev) => ({
                                      ...prev,
                                      subject: e.target.value,
                                    }))
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      handleSubmit();
                                    }
                                  }}
                                  onBlur={() => {
                                    setTagging({
                                      active: false,
                                      boxIdx: null,
                                      subject: "",
                                    });
                                    setHoveredFaceIdx(null);
                                  }}
                                />
                              </TagForm>
                            );
                          })()
                        : null}
                    </React.Fragment>
                  );
                })}
            </ImgWrapper>
            {items.length > 1 ? (
              <ArrowCircleLeft onClick={() => handleClick(true)}>
                <ArrowLeftIconWrapper />
              </ArrowCircleLeft>
            ) : null}
          </ImgContainer>
        );
      case "pdf":
        return (
          <PdfWrapper>
            <ZoomControl style={{ display: loadingImg ? "none" : "flex" }}>
              <StyledZoomOut
                onClick={() =>
                  setScale((prev) => {
                    return prev >= 0.5 ? prev - 0.25 : prev;
                  })
                }
              />
              <StyledZoom onClick={() => setScale(1)} />

              <StyledZoomIn
                onClick={() =>
                  setScale((prev) => {
                    return prev < 2 ? prev + 0.25 : prev;
                  })
                }
              />
            </ZoomControl>

            <ImgContainer>
              {items.length > 1 ? (
                <ArrowCircleRight onClick={() => handleClick(false)}>
                  <ArrowRightIconWrapper />
                </ArrowCircleRight>
              ) : null}
              <Stack
                style={{
                  display: loadingImg ? "flex" : "none",
                  width: "100%",
                }}
                alignItems="center"
              >
                <CircularProgress
                  variant="determinate"
                  value={progress}
                  style={{ display: loadingImg ? "block" : "none" }}
                />
              </Stack>
              <ImgWrapper style={{ display: loadingImg ? "none" : "block" }}>
                <StyledDocument
                  onTouchStart={(e) => {
                    setStartX(e.changedTouches[0].screenX);
                  }}
                  onTouchEnd={(e) => {
                    swipeHandler();
                  }}
                  onTouchMove={(e) => {
                    setEndX(e.targetTouches[0].clientX);
                  }}
                  file={S3OriginalsUrl}
                  options={
                    view.dcAccessRights === "private"
                      ? { withCredentials: true }
                      : undefined
                  }
                  onLoadProgress={onDocumentLoading}
                  onLoadSuccess={onDocumentLoadSuccess}
                >
                  {showAllPages ? showAll(scale) : null}
                </StyledDocument>
              </ImgWrapper>
              {items.length > 1 ? (
                <ArrowCircleLeft onClick={() => handleClick(true)}>
                  <ArrowLeftIconWrapper />
                </ArrowCircleLeft>
              ) : null}
            </ImgContainer>
          </PdfWrapper>
        );

      default:
        return null;
    }
  };

  // inside Viewer component

  const getLink = (view) => {
    return (
      "mailto:archion.beeri+comment@gmail.com?subject=" +
      view.dcTitle +
      " מזהה: " +
      view.id
    );
  };

  return (
    <GeneralGrid showInfoView={showInfoView}>
      {view && (
        <>
          <ViewerInfo
            data={view}
            setShowInfoView={setShowInfoView}
            showInfoView={showInfoView}
            editInfo={editInfo}
            setEditInfo={setEditInfo}
            setData={setView}
            handleCloseClick={handleCloseClick}
          />
          <Container>
            <Header>
              <Icons>
                {showInfoView ? null : (
                  <StyledInfoOutlinedIcon onClick={handleInfoClick} />
                )}

                <ActionIcons>
                  <StyledGetAppIconLink
                    href={getAssetUrl(view)}
                    download={view.id + "." + view.dcFormat}
                    target={"_blank"}
                  >
                    <StyledGetAppIcon />
                  </StyledGetAppIconLink>
                  <StyledShare onClick={() => setShareOpen(true)} />
                  <StyledCommentLink
                    user={isAuthenticated && !editInfo}
                    href={getLink(view)}
                  >
                    <StyledComment />
                  </StyledCommentLink>

                  {isAuthenticated && !editInfo ? (
                    <StyledEditInfoLink onClick={handleEditInfoClick}>
                      <StyledEditIcon />
                    </StyledEditInfoLink>
                  ) : null}
                </ActionIcons>
                <StyledArrowBackOutlinedIcon onClick={handleCloseClick} />
              </Icons>
              <ItemDetails>
                <Title>{view.dcTitle}</Title>
                <AddDateTitle dateTime={view.dcDate} />
              </ItemDetails>
            </Header>
            {mainAsset(view, startX, setStartX, endX, setEndX)}
          </Container>
        </>
      )}
      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        url={viewUrl}
      />
    </GeneralGrid>
  );
}

const StyledArrowBackOutlinedIcon = styled(ArrowBackOutlinedIcon)`
  cursor: pointer;

  width: 24px;
  height: 24px;
  margin-left: 16px;
`;

const StyledGetAppIcon = styled(GetAppIcon)`
  width: 24px;
  height: 24px;
`;

const StyledComment = styled(CommentOutlinedIcon)`
  width: 24px;
  height: 24px;
`;

const StyledShare = styled(ShareIcon)`
  width: 24px;
  height: 24px;
  margin-right: 16px;
  color: unset;
  cursor: pointer;
`;

const ActionIcons = styled.div`
  display: flex;

  margin-left: auto;
`;
const StyledEditIcon = styled(EditIcon)`
  width: 24px;
  height: 24px;
`;

const StyledCommentLink = styled.a`
  cursor: pointer;
  width: 24px;
  height: 24px;
  margin-left: ${(props) => (props.user ? "4px" : "auto")};
  margin-right: 16px;
  color: unset;
`;

const StyledEditInfoLink = styled.a`
  cursor: pointer;
  width: 24px;
  height: 24px;
  margin-left: auto;
  margin-right: 16px;
  color: unset;
`;

const StyledGetAppIconLink = styled.a`
  margin-right: 16px;
  color: unset;
`;

const StyledInfoOutlinedIcon = styled(InfoOutlinedIcon)`
  width: 24px;
  height: 24px;
  margin-right: 16px;
  cursor: pointer;
`;

const GeneralGrid = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  display: grid;
  height: 100%;
  overflow: hidden;
  grid-template-columns: ${(props) => (props.showInfoView ? "1fr" : "0fr 1fr")};

  @media (min-width: 500px) {
    grid-template-columns: ${(props) =>
      props.showInfoView ? "30% 70%" : "0fr 1fr"};
  }
`;

const Container = styled.div`
  width: 100%;
  background-color: black;
  z-index: 200;
  overflow-y: overlay;
  margin-bottom: 0;
  overflow-x: hidden;
  height: 100vh;
`;

const Header = styled.div`
  width: 100%;
`;

const Icons = styled.div`
  display: flex;
  height: 24px;
  margin-top: 16px;
  color: white;
  width: 100%;
  @media (min-width: 800px) {
    margin-top: 16px;
    margin-bottom: 8px;
  }
`;

const ItemDetails = styled.div`
  @media (min-width: 800px) {
    display: none;
  }
`;
const Title = styled.div`
  display: flex;
  flex-direction: row;
  margin: 16px 24px 0px 24px;
  font-family: Rubik;
  font-size: 24px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: normal;
  text-align: right;
  color: #fff;
  direction: rtl;
`;

const BoundingBox = styled.div`
  position: absolute;
  inset: 0;
  border: 1.5px solid #14fc03;
  box-sizing: border-box;
  pointer-events: auto;
  opacity: 0;
  transition: opacity 0.15s ease;
`;

const TAG_FONT_SIZE = "16px";

const SubjectLabel = styled.div`
  position: absolute;
  left: 0;
  top: 100%;
  width: 100%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: ${TAG_FONT_SIZE};
  font-family: Rubik, sans-serif;
  text-align: center;
  box-sizing: border-box;
  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: auto;
  transform: translateY(4px);
`;

const PendingTagIndicator = styled(CircularProgress).attrs({
  color: "inherit",
})`
  position: absolute;
  left: 50%;
  top: calc(100% + 8px);
  transform: translateX(-50%);
  pointer-events: none;

  && {
    color: #fff;
  }
`;

const FaceRegion = styled.div`
  position: absolute;
  pointer-events: auto;
  &:hover ${BoundingBox} {
    opacity: 1;
  }
  &:hover ${SubjectLabel} {
    opacity: 1;
  }
`;

const TagForm = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: space-between;
  background: rgba(0, 0, 0, 0.6);
  padding: 4px 6px;
  border-radius: 4px;
  z-index: 201;
  top: 100%;
  left: 0;
  transform: translateY(4px);
`;

const TagInput = styled(TextField)`
  flex: 1;
  min-width: 0;

  .MuiOutlinedInput-root {
    background-color: rgba(0, 0, 0, 0.6);
    border-radius: 4px;
    padding: 0;
  }

  .MuiOutlinedInput-input {
    padding: 4px 6px;
    font-size: ${TAG_FONT_SIZE};
    font-family: Rubik, sans-serif;
    text-align: center;
    color: #fff;
  }

  .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  &:hover .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  .Mui-focused .MuiOutlinedInput-notchedOutline {
    border: none;
  }
`;

const PdfWrapper = styled.div`
  height: 100%;
`;

const ZoomControl = styled.div`
  width: 112px;
  height: 39px;
  margin-right: calc(50% - 56px);
  top: 90%;
  border-radius: 4px;
  background-color: #d8d8d8;
  display: flex;
  align-items: center;
  z-index: 100;
  position: sticky;
`;

const StyledZoomIn = styled(AddIcon)`
  width: 24px;
  height: 24px;
  margin: 0 0 0 8px;
  padding: 5px;
  cursor: pointer;
`;
const StyledZoomOut = styled(RemoveIcon)`
  width: 24px;
  height: 24px;
  margin: 0 8px 0 0;
  padding: 11px 5px;
  cursor: pointer;
`;

const StyledZoom = styled(ZoomOutIcon)`
  width: 17px;
  height: 17px;
  cursor: pointer;
`;

const StyledDocument = styled(Document)`
  width: 100%;
  background-color: black;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledPage = styled(Page)`
  position: fixed;
  margin-bottom: 10px;
  overflow: hidden;
  .react-pdf__Page__canvas {
    margin: 0 auto;
    height: auto !important;
    max-width: 100% !important;
  }
`;

const Img = styled.img`
  width: 100%;
  object-fit: ${(props) => (props.zoom ? "cover" : "contain")};
  height: auto;
  max-height: ${(props) => (props.zoom ? "none" : "100%")};
  display: flex;
  align-self: start;
  overflow: ${(props) => (props.zoom ? "scroll" : "hidden")};
  cursor: ${(props) => (props.zoom ? "zoom-out" : "zoom-in")};

  @media (min-width: 500px) {
    width: calc(100% - 120px);
  }
`;

const Audio = styled(AudioPlayer)`
  width: 100%;
  top: 50vh;
  direction: ltr;

  @media (min-width: 500px) {
    height: 150px;
    width: 700px;
  }
`;

const Video = styled.video`
  width: 100%;
  object-fit: contain;
  height: auto;
  max-height: 100%;
  display: flex;
  align-self: start;

  @media (min-width: 500px) {
    width: calc(100% - 120px);
  }
`;

const ImgContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  overflow: hidden;
  @media (min-width: 500px) {
    position: relative;
    height: calc(100% - 100px);
  }
`;

const ImgWrapper = styled.div`
  overflow: auto;
  display: flex;
  position: relative;
  align-items: center;
  align-self: flex-start;
  width: 100%;
  justify-content: center;
  @media (min-width: 500px) {
    height: 100%;
  }
`;

const ArrowCircleRight = styled.div`
  position: absolute;
  top: 50vh;

  height: 40px;
  width: 40px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  margin: 10px;
  mix-blend-mode: difference;
  z-index: 200;
  display: none;
  @media (min-width: 500px) {
    display: flex;
  }
`;

const ArrowCircleLeft = styled.div`
  position: absolute;
  top: 50vh;
  left: 0px;
  height: 40px;
  width: 40px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  margin: 10px;
  mix-blend-mode: difference;
  z-index: 200;
  display: none;
  @media (min-width: 500px) {
    display: flex;
  }
`;

const ArrowRightIconWrapper = styled(ArrowRightIcon)`
  color: white;
  cursor: pointer;
`;

const ArrowLeftIconWrapper = styled(ArrowLeftIcon)`
  color: white;
  cursor: pointer;
`;
